import * as React from 'react';

import styles from './CatalogLayout.scss';
import { CatalogErrorPage } from 'components/catalogs/components/CatalogErrorPage/CatalogErrorPage';
import { Footer } from 'components/catalogs/components/Footer/Footer';
import { Header } from 'components/catalogs/components/Header/Header';
import { extractStatusCode } from 'components/catalogs/libs/catalogApiClient';
import { saveSerialCodeToSession } from 'components/catalogs/libs/serialCode';
import { useCatalogCategories } from 'components/catalogs/hooks/useCatalogCategories';

type CatalogSummary = import('components/catalogs/libs/catalogApiClient').CatalogSummary;
type CatalogCategory = import('components/catalogs/hooks/useCatalogCategories').CatalogCategory;

type Props = React.PropsWithChildren<{
  catalogCode: string;
}> &
  Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>;

export type CatalogLayoutContextValue = {
  catalog: CatalogSummary | null;
  categories: CatalogCategory[] | null;
  hasError: boolean;
  pageError: CatalogPageError | null;
  reportPageError: (error: unknown) => void;
  reportNotFound: () => void;
  clearPageError: () => void;
};

export type CatalogPageError = {
  kind: 'not_found' | 'generic_error';
  status: number | null;
};

const CatalogLayoutContext = React.createContext<CatalogLayoutContextValue>({
  catalog: null,
  categories: null,
  hasError: false,
  pageError: null,
  reportPageError: () => undefined,
  reportNotFound: () => undefined,
  clearPageError: () => undefined,
});

export const useCatalogLayoutContext = (): CatalogLayoutContextValue => React.useContext(CatalogLayoutContext);

export const CatalogLayout = ({ catalogCode, className, children, ...rest }: Props): JSX.Element => {
  const [pageError, setPageError] = React.useState<CatalogPageError | null>(null);

  React.useEffect(() => {
    const urlSerial = new URLSearchParams(window.location.search).get('serial_code');
    if (urlSerial) {
      saveSerialCodeToSession(urlSerial);
    }
  }, []);

  const clearPageError = React.useCallback((): void => {
    setPageError(null);
  }, []);

  const reportNotFound = React.useCallback((): void => {
    setPageError((prev) => (prev?.kind === 'not_found' ? prev : { kind: 'not_found', status: 404 }));
  }, []);

  const reportPageError = React.useCallback((error: unknown): void => {
    const status = extractStatusCode(error);
    const next: CatalogPageError = status === 404 ? { kind: 'not_found', status } : { kind: 'generic_error', status };

    setPageError((prev) => {
      if (prev?.kind === 'not_found') {
        return prev;
      }
      if (next.kind === 'not_found') {
        return next;
      }
      return prev ?? next;
    });
  }, []);

  const previousCatalogCodeRef = React.useRef(catalogCode);
  React.useEffect(() => {
    if (previousCatalogCodeRef.current === catalogCode) {
      return;
    }
    previousCatalogCodeRef.current = catalogCode;
    setPageError(null);
  }, [catalogCode]);

  const { catalog, categories, hasError } = useCatalogCategories({
    catalogCode,
    onError: reportPageError,
  });

  return (
    <CatalogLayoutContext.Provider
      value={{ catalog, categories, hasError, pageError, reportPageError, reportNotFound, clearPageError }}
    >
      <div
        {...rest}
        className={[styles.page, className].filter(Boolean).join(' ')}
        data-catalog-code={catalogCode}
      >
        <Header
          catalogCode={catalogCode}
          categories={categories}
          hasError={hasError}
        />
        <main className={styles.main}>
          {pageError?.kind === 'not_found' ? <CatalogErrorPage variant="not_found" /> : null}
          {pageError?.kind === 'generic_error' ? <CatalogErrorPage variant="generic_error" /> : null}
          {!pageError ? children : null}
        </main>
        <Footer
          catalogCode={catalogCode}
          categories={categories}
          hasError={hasError}
        />
      </div>
    </CatalogLayoutContext.Provider>
  );
};
